import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { T, useTranslate } from '@tolgee/react';
import { Facebook, Instagram, Youtube } from 'lucide-react';
import apiManager from '../../lib/api';

// reCAPTCHA v3 site key
const RECAPTCHA_SITE_KEY = '6Lcg31gsAAAAALZgvPJaTe9t8_KNHOz2WV9gC32i';

// Declare grecaptcha on window
declare global {
  interface Window {
    grecaptcha: {
      ready: (callback: () => void) => void;
      execute: (siteKey: string, options: { action: string }) => Promise<string>;
    };
  }
}

const Contacto = () => {
  const { t } = useTranslate();
  const [formData, setFormData] = useState({
    nombre: '',
    email: '',
    asunto: '',
    mensaje: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [recaptchaLoaded, setRecaptchaLoaded] = useState(false);

  // Load reCAPTCHA script
  useEffect(() => {
    if (typeof window !== 'undefined' && !document.querySelector(`script[src*="recaptcha"]`)) {
      const script = document.createElement('script');
      script.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`;
      script.async = true;
      script.defer = true;
      script.onload = () => {
        window.grecaptcha.ready(() => {
          setRecaptchaLoaded(true);
        });
      };
      document.head.appendChild(script);
    } else if (window.grecaptcha) {
      window.grecaptcha.ready(() => {
        setRecaptchaLoaded(true);
      });
    }
  }, []);

  // Get reCAPTCHA token
  const getRecaptchaToken = useCallback(async (): Promise<string | null> => {
    if (!recaptchaLoaded || !window.grecaptcha) {
      return null;
    }
    try {
      const token = await window.grecaptcha.execute(RECAPTCHA_SITE_KEY, { action: 'contact' });
      return token;
    } catch (error) {
      console.error('Error getting reCAPTCHA token:', error);
      return null;
    }
  }, [recaptchaLoaded]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prevData => ({
      ...prevData,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError('');

    try {
      // Get reCAPTCHA token
      const recaptchaToken = await getRecaptchaToken();
      if (!recaptchaToken) {
        setSubmitError(t('contact.form.error.recaptcha', 'Error de verificación de seguridad. Por favor, recarga la página e inténtalo de nuevo.'));
        setIsSubmitting(false);
        return;
      }

      // Send form data to API
      const response = await apiManager.contact.sendMessage({
        ...formData,
        recaptchaToken
      });

      if (response.success) {
        setSubmitSuccess(true);
        setFormData({
          nombre: '',
          email: '',
          asunto: '',
          mensaje: ''
        });
      } else {
        setSubmitError(response.error?.message || t('contact.form.error.generic', 'Ha ocurrido un error al enviar el mensaje. Por favor, inténtalo de nuevo.'));
      }
    } catch (error) {
      console.error('Error sending contact form:', error);
      setSubmitError(t('contact.form.error.generic', 'Ha ocurrido un error al enviar el mensaje. Por favor, inténtalo de nuevo.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Contacto | Jarad</title>
        <meta name="description" content="Contacta con Jarad. Estamos aquí para ayudarte con cualquier duda o consulta sobre nuestros productos." />
      </Helmet>

      <div style={{ backgroundColor: '#f2ede4' }} className="min-h-screen">
        {/* Hero Section */}
        <div className="py-24">
          <div className="content-container">
            <motion.div
              className="text-center"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h1 className="text-4xl md:text-6xl font-extrabold mb-6 tracking-tight" style={{ color: '#0d0d0d' }}>
                <T keyName="contact.title">Contacto</T>
              </h1>
              <p className="text-xl md:text-2xl font-light max-w-3xl mx-auto text-gray-600">
                <T keyName="contact.subtitle">Estamos aquí para ayudarte con cualquier duda o consulta sobre nuestros productos</T>
              </p>
            </motion.div>
          </div>
        </div>

        {/* Main Content */}
        <div className="py-16">
          <div className="content-container">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
              {/* Formulario de contacto */}
              <motion.div
                className="lg:col-span-7"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                <div className="bg-white p-8 rounded-2xl shadow-md">
                  {submitSuccess ? (
                    <motion.div
                      className="text-center py-8"
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.5 }}
                    >
                      <div
                        className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
                        style={{ backgroundColor: '#f6cf02' }}
                      >
                        <svg className="h-8 w-8" style={{ color: '#0d0d0d' }} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <h3 className="text-2xl font-bold mb-2" style={{ color: '#0d0d0d' }}>
                        <T keyName="contact.form.success.title">¡Mensaje enviado!</T>
                      </h3>
                      <p className="text-gray-600 mb-6">
                        <T keyName="contact.form.success.message">Gracias por contactarnos. Te responderemos pronto.</T>
                      </p>
                      <button
                        type="button"
                        onClick={() => setSubmitSuccess(false)}
                        className="px-6 py-3 rounded-full font-medium transition-all border-2"
                        style={{
                          backgroundColor: '#f6cf02',
                          color: '#0d0d0d',
                          borderColor: '#0d0d0d'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#0d0d0d';
                          e.currentTarget.style.color = '#f6cf02';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = '#f6cf02';
                          e.currentTarget.style.color = '#0d0d0d';
                        }}
                      >
                        <T keyName="contact.form.success.new_message">Enviar otro mensaje</T>
                      </button>
                    </motion.div>
                  ) : (
                    <form onSubmit={handleSubmit} className="space-y-6">
                      {submitError && (
                        <motion.div
                          className="p-4 rounded-2xl text-sm"
                          style={{ backgroundColor: '#fee2e2', color: '#dc2626' }}
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                        >
                          {submitError}
                        </motion.div>
                      )}

                      {/* Nombre */}
                      <div>
                        <label htmlFor="nombre" className="block text-sm font-medium mb-2" style={{ color: '#0d0d0d' }}>
                          <T keyName="contact.form.name">Nombre completo</T>
                        </label>
                        <input
                          type="text"
                          name="nombre"
                          id="nombre"
                          value={formData.nombre}
                          onChange={handleChange}
                          className="block w-full px-4 py-3 border-2 border-gray-200 rounded-2xl focus:border-gray-400 outline-none transition-colors"
                          style={{ color: '#0d0d0d' }}
                          required
                        />
                      </div>

                      {/* Email */}
                      <div>
                        <label htmlFor="email" className="block text-sm font-medium mb-2" style={{ color: '#0d0d0d' }}>
                          <T keyName="contact.form.email">Correo electrónico</T>
                        </label>
                        <input
                          type="email"
                          name="email"
                          id="email"
                          value={formData.email}
                          onChange={handleChange}
                          className="block w-full px-4 py-3 border-2 border-gray-200 rounded-2xl focus:border-gray-400 outline-none transition-colors"
                          style={{ color: '#0d0d0d' }}
                          required
                        />
                      </div>

                      {/* Asunto */}
                      <div>
                        <label htmlFor="asunto" className="block text-sm font-medium mb-2" style={{ color: '#0d0d0d' }}>
                          <T keyName="contact.form.subject">Asunto</T>
                        </label>
                        <select
                          id="asunto"
                          name="asunto"
                          value={formData.asunto}
                          onChange={handleChange}
                          className="block w-full px-4 py-3 border-2 border-gray-200 rounded-2xl focus:border-gray-400 outline-none transition-colors"
                          style={{ color: '#0d0d0d' }}
                          required
                        >
                          <option value="">{t('contact.form.subject_placeholder', 'Selecciona un asunto')}</option>
                          <option value="consulta_producto">{t('contact.form.subject_options.product', 'Consulta sobre producto')}</option>
                          <option value="pedido">{t('contact.form.subject_options.order', 'Información de pedido')}</option>
                          <option value="devolucion">{t('contact.form.subject_options.return', 'Devolución o cambio')}</option>
                          <option value="otro">{t('contact.form.subject_options.other', 'Otro')}</option>
                        </select>
                      </div>

                      {/* Mensaje */}
                      <div>
                        <label htmlFor="mensaje" className="block text-sm font-medium mb-2" style={{ color: '#0d0d0d' }}>
                          <T keyName="contact.form.message">Mensaje</T>
                        </label>
                        <textarea
                          id="mensaje"
                          name="mensaje"
                          rows={6}
                          value={formData.mensaje}
                          onChange={handleChange}
                          className="block w-full px-4 py-3 border-2 border-gray-200 rounded-2xl focus:border-gray-400 outline-none transition-colors resize-none"
                          style={{ color: '#0d0d0d' }}
                          required
                        ></textarea>
                      </div>

                      <div className="pt-4">
                        <motion.button
                          type="submit"
                          disabled={isSubmitting || !recaptchaLoaded}
                          className="w-full sm:w-auto px-8 py-3 rounded-full font-medium transition-all border-2 disabled:opacity-50 disabled:cursor-not-allowed"
                          style={{
                            backgroundColor: (isSubmitting || !recaptchaLoaded) ? '#9CA3AF' : '#f6cf02',
                            color: '#0d0d0d',
                            borderColor: (isSubmitting || !recaptchaLoaded) ? '#9CA3AF' : '#0d0d0d'
                          }}
                          whileHover={(!isSubmitting && recaptchaLoaded) ? { scale: 1.05 } : {}}
                          whileTap={(!isSubmitting && recaptchaLoaded) ? { scale: 0.95 } : {}}
                          onMouseEnter={(e) => {
                            if (!isSubmitting && recaptchaLoaded) {
                              e.currentTarget.style.backgroundColor = '#0d0d0d';
                              e.currentTarget.style.color = '#f6cf02';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!isSubmitting && recaptchaLoaded) {
                              e.currentTarget.style.backgroundColor = '#f6cf02';
                              e.currentTarget.style.color = '#0d0d0d';
                            }
                          }}
                        >
                          {isSubmitting ? (
                            <span className="flex items-center justify-center">
                              <svg className="animate-spin -ml-1 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              <T keyName="contact.form.sending">Enviando...</T>
                            </span>
                          ) : (
                            <T keyName="contact.form.submit">Enviar mensaje</T>
                          )}
                        </motion.button>

                        {/* reCAPTCHA notice */}
                        <p className="mt-4 text-xs text-gray-500">
                          <T keyName="contact.form.recaptcha_notice">
                            Este sitio está protegido por reCAPTCHA y se aplican la
                          </T>{' '}
                          <a
                            href="https://policies.google.com/privacy"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="underline hover:text-gray-700"
                          >
                            <T keyName="contact.form.privacy_policy">Política de Privacidad</T>
                          </a>{' '}
                          <T keyName="contact.form.and">y</T>{' '}
                          <a
                            href="https://policies.google.com/terms"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="underline hover:text-gray-700"
                          >
                            <T keyName="contact.form.terms">Términos de Servicio</T>
                          </a>{' '}
                          <T keyName="contact.form.of_google">de Google.</T>
                        </p>
                      </div>
                    </form>
                  )}
                </div>
              </motion.div>

              {/* Información de contacto */}
              <motion.div
                className="lg:col-span-5"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
              >
                <div className="bg-white p-8 rounded-2xl shadow-md h-fit">
                  <h2 className="text-2xl font-bold mb-6" style={{ color: '#0d0d0d' }}>
                    <T keyName="contact.info.title">Información de contacto</T>
                  </h2>

                  <div className="space-y-6">
                    {/* Dirección */}
                    <div className="flex items-start space-x-4">
                      <div
                        className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: '#f6cf02' }}
                      >
                        <svg className="w-6 h-6" style={{ color: '#0d0d0d' }} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                      </div>
                      <div>
                        <h3 className="font-bold mb-1" style={{ color: '#0d0d0d' }}>
                          <T keyName="contact.info.address.title">Dirección</T>
                        </h3>
                        <address className="text-gray-600 not-italic">
                          C/ Ctra. de Utrera, 19-A<br />
                          41760 El Coronil<br />
                          Sevilla (España)
                        </address>
                      </div>
                    </div>

                    {/* Email */}
                    <div className="flex items-start space-x-4">
                      <div
                        className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: '#f6cf02' }}
                      >
                        <svg className="w-6 h-6" style={{ color: '#0d0d0d' }} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                      </div>
                      <div>
                        <h3 className="font-bold mb-1" style={{ color: '#0d0d0d' }}>
                          <T keyName="contact.info.email.title">Email</T>
                        </h3>
                        <a href="mailto:info@jaradpetfood.com" className="text-gray-600 hover:text-gray-800 transition-colors">
                          info@jaradpetfood.com
                        </a>
                      </div>
                    </div>

                    {/* Teléfono */}
                    <div className="flex items-start space-x-4">
                      <div
                        className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: '#f6cf02' }}
                      >
                        <svg className="w-6 h-6" style={{ color: '#0d0d0d' }} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                        </svg>
                      </div>
                      <div>
                        <h3 className="font-bold mb-1" style={{ color: '#0d0d0d' }}>
                          <T keyName="contact.info.phone.title">Teléfono</T>
                        </h3>
                        <a href="tel:+34955836823" className="text-gray-600 hover:text-gray-800 transition-colors">
                          +34 955 83 68 23
                        </a>
                      </div>
                    </div>

                    {/* Horario */}
                    <div className="flex items-start space-x-4">
                      <div
                        className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: '#f6cf02' }}
                      >
                        <svg className="w-6 h-6" style={{ color: '#0d0d0d' }} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <div>
                        <h3 className="font-bold mb-1" style={{ color: '#0d0d0d' }}>
                          <T keyName="contact.info.hours.title">Horario</T>
                        </h3>
                        <div className="text-gray-600">
                          <p><T keyName="contact.info.hours.weekdays">Lun-Vie: 8:00h - 14:00h / 15:30h - 18:00h</T></p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Redes sociales */}
                  <div className="mt-8 pt-6 border-t border-gray-200">
                    <h3 className="font-bold mb-4" style={{ color: '#0d0d0d' }}>
                      <T keyName="contact.info.social.title">Síguenos</T>
                    </h3>
                    <div className="flex space-x-4">
                      {/* Instagram */}
                      <motion.a
                        href="https://www.instagram.com/jaradpetfood/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-10 h-10 rounded-full flex items-center justify-center transition-all hover:scale-110"
                        style={{ backgroundColor: '#f6cf02' }}
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        <span className="sr-only">Instagram</span>
                        <Instagram className="w-5 h-5" style={{ color: '#0d0d0d' }} />
                      </motion.a>
                      {/* Facebook */}
                      <motion.a
                        href="https://www.facebook.com/jaradpetfood"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-10 h-10 rounded-full flex items-center justify-center transition-all hover:scale-110"
                        style={{ backgroundColor: '#f6cf02' }}
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        <span className="sr-only">Facebook</span>
                        <Facebook className="w-5 h-5" style={{ color: '#0d0d0d' }} />
                      </motion.a>
                      {/* Youtube */}
                      <motion.a
                        href="https://www.youtube.com/@JaradPetFood"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-10 h-10 rounded-full flex items-center justify-center transition-all hover:scale-110"
                        style={{ backgroundColor: '#f6cf02' }}
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        <span className="sr-only">YouTube</span>
                        <Youtube className="w-5 h-5" style={{ color: '#0d0d0d' }} />
                      </motion.a>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Contacto;